"use client";

import type { Locale } from "@/i18n.config";
import { GalleryTemplateOne } from "./components/GalleryTemplateOne";
import { GalleryTemplateTwo } from "./components/GalleryTemplateTwo";
import { GalleryTemplateThree } from "./components/GalleryTemplateThree";
import { GalleryTemplateFour } from "./components/GalleryTemplateFour";
import { HeroBanner } from "./components/HeroBanner";
import { PopularTopics } from "./components/PopularTopics";

interface BlogListClientProps {
  locale: Locale;
  initialConfig?: any;
  initialBlogs?: any[];
  resolvedSections?: any[];
  allLabel?: string;
  pageTitle?: string;
}

// Helper to prevent OptimizedImage from crashing on raw IDs (numbers)
export const safeImage = (img: any) => {
  if (!img) return null;
  if (typeof img === "number") return null;
  if (typeof img === "string" && !img.includes("/") && !img.includes(".")) return null;
  return img;
};

export function BlogListClient({
  locale,
  initialConfig,
  initialBlogs,
  resolvedSections,
  allLabel,
  pageTitle,
}: BlogListClientProps) {
  const config = initialConfig || null;
  const blogs = initialBlogs || [];
  const sections = resolvedSections || [];

  const featured = config?.featuredPost;
  const featuredId = featured
    ? typeof featured === "object"
      ? featured.id
      : featured
    : null;
  const heroPost = featuredId
    ? blogs.find((p: any) => String(p.id) === String(featuredId)) ||
      (typeof featured === "object" ? featured : null)
    : blogs[0] || null;

  const hero = {
    tag: config?.heroTitle || (locale === "zh" ? "本周推荐" : "FEATURED"),
    post: heroPost,
    backgroundImage: config?.heroBackgroundImage,
  };

  const categoryTabs = config?.kbCategoryTabs || [];

  return (
    <div
      className="min-h-screen bg-[#F6F4ED] font-lexend-deca antialiased selection:bg-[#ff4848] selection:text-white"
      data-header-theme="light"
    >
      <h1 className="sr-only">{pageTitle || "Busrom Knowledge Base & Blog"}</h1>

      {/* 1. HERO BANNER - SPLIT LAYOUT */}
      <HeroBanner hero={hero} locale={locale} />

      {/* 2. POPULAR TOPICS - RESTORED PREMIUM HOVER EFFECT */}
      <PopularTopics
        config={config}
        locale={locale}
        allLabel={allLabel}
        blogs={blogs}
        categoryTabs={categoryTabs}
        hydratedPosts={{}}
      />

      {/* 3. DYNAMIC SECTIONS - items resolved server-side, counts match CMS */}
      {sections.length > 0
        ? sections.map((section: any, idx: number) =>
            section.items?.length > 0 ? (
              <DynamicSection key={idx} section={section} locale={locale} />
            ) : null,
          )
        : config && (
            <div className="py-24 text-center">
              <p className="text-gray-400 italic">
                No sections found for this configuration.
              </p>
            </div>
          )}
    </div>
  );
}

function DynamicSection({ section, locale }: any) {
  const template = section.template;
  const items = section.items || [];

  if (!template) return null;

  switch (template) {
    case "template1":
      return (
        <GalleryTemplateOne section={section} items={items} locale={locale} />
      );
    case "template2":
      return (
        <GalleryTemplateTwo section={section} items={items} locale={locale} />
      );
    case "template3":
      return (
        <GalleryTemplateThree section={section} items={items} locale={locale} />
      );
    case "template4":
      return (
        <GalleryTemplateFour section={section} items={items} locale={locale} />
      );
    default:
      return null;
  }
}
